import { ComponentProps, FC } from 'react';
import { useSelector } from 'react-redux';
import { Box, Checkbox, Colors, Input, Svg, Typography } from '@altermaind/web/ui';
import { getModeTheme } from '@/_shared/styles/state/selectors';

type Props = {
  label: string;
  value: string | boolean | string[] | number;
  isCheckbox?: boolean;
  avoidRender?: boolean;
  valueAsCopy?: boolean;
  containerProps?: ComponentProps<typeof Box>;
  svg?: {
    name: string;
    props?: Omit<ComponentProps<typeof Svg>, 'name'>;
  };
};

const NotArrayComponent: FC<Omit<Props, 'label'>> = ({ value, isCheckbox, valueAsCopy, svg }) => {
  if (valueAsCopy) {
    return (
      <Box display='flex' flexDirection='row' justifyContent='space-between'>
        <Typography variant='p2/light' color='primary/text'>{`${value}`}</Typography>
        {svg && (
          // eslint-disable-next-line local-rules/no-svg-spread, react/jsx-props-no-spreading
          <Svg name={svg.name} {...svg.props} />
        )}
      </Box>
    );
  }
  return !isCheckbox ? (
    <Input value={value as string} disabled />
  ) : (
    <Checkbox checked={value as boolean} disabled />
  );
};

export const OutputValue: FC<Props> = ({
  label,
  value,
  isCheckbox,
  valueAsCopy,
  containerProps,
  svg,
  avoidRender = false,
}) => {
  const mode = useSelector(getModeTheme());
  if (avoidRender) {
    return null;
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Box display='flex' flexDirection='column' flex={1} mr='s' {...containerProps}>
      <Typography variant='p2/light' mb='xxs' color={valueAsCopy ? 'grey/light' : undefined}>
        {label}
      </Typography>
      {typeof value === 'object' ? (
        <Typography
          variant='p2/light'
          mb='xxs'
          bg={`grey/${mode}/background` as keyof Colors}
          dangerouslySetInnerHTML={{ __html: value.join('<br /><br />') }}
          maxHeight={150}
          overflow='auto'
          padding={15}
          borderRadius={25}
          opacity={0.5}
          color='primary/text'
        />
      ) : (
        <NotArrayComponent
          isCheckbox={isCheckbox}
          value={value}
          valueAsCopy={valueAsCopy}
          svg={svg}
        />
      )}
    </Box>
  );
};
